import daneaLogo from '../../assets/images/danea-logo.png';
import styles from './ActivationCard.module.css';
import { Box, IconButton, Stack } from "@mui/material";
import React from "react";
import { MdBusiness, MdMenuBook } from "react-icons/md";
import { TbLicense, TbLicenseOff } from "react-icons/tb";
import { useErrorNotify, useWarningNotify, useLoaderContext } from "../../contexts";
import { useCompanyDetailsPage } from "../../features/CompanyDetails/CompanyDetailsPage";
import { useActivationDetailsView } from "../../features/CompanyLicenses/ActivationDetailsPage";
import { AppInCloudRoles } from "../../helpers/api.helpers";
import { useViewLoaders } from "../../utilities/useViewLoaders";
import { ActivationDTO, GetActivations, RemoteAccess } from '../../helpers/activations.services';
import { VdsTooltip } from '../../@vds';
import { pushLoginClickEvent } from '../../utilities/eventsGTM';

const ACCESS_RETRY_TIMEOUT = window.FrontendSettings?.ACCESS_RETRY_TIMEOUT ?? 60000;

const lastClicks: Record<string, number> = {};
const openedWindows: Record<string, Window | null> = {};
const activeSessions: Record<string, boolean> = {};

type ActivationCardProps = ActivationDTO & {
    onRefreshActivations?: () => void | Promise<void>;
};

export const ActivationCard: React.FC<ActivationCardProps> = (props) => {

    const {
        companyOid,
        companyFiscalCode,
        companyClientCode,
        companyName,
        companyVatCode,
        roleName,
        activationOid,
        activeLicense,
        status,
        cardMessage,
        onRefreshActivations,
    } = props;

    const callErrorNotify = useErrorNotify();
    const callInfoNotify = useWarningNotify();
    const setLoader = useLoaderContext();

    const companyLoader = useCompanyDetailsPage();
    const activationLoader = useActivationDetailsView();

    const views = useViewLoaders({
        company: companyLoader,
        activation: activationLoader,
    });

    const accessAllowed = activeLicense && (status === 'Active' || status === 'Warning');

    const refreshSafe = () => {
        onRefreshActivations?.();
    };

    /**
       * Nuova logica richiesta:
       * 1) prima di aprire il RemoteAccess, richiama getActivations
       * 2) verifica lo stato dell'attivazione cliccata
       * 3) se blocked -> aggiorna attivazioni e NON redirect
       * 4) altrimenti -> fai redirect e aggiorna attivazioni
       */
    const handleClick = async () => {
        if (!accessAllowed) return;

        if (activeSessions[activationOid]) {
            callInfoNotify(
                "È già presente una sessione Easyfatt aperta per questa azienda."
            );
            return;
        }

        const now = Date.now();
        const lastClick = lastClicks[activationOid];

        if (lastClick) {
            const elapsed = now - lastClick;

            if (elapsed < ACCESS_RETRY_TIMEOUT) {
                const remainingSeconds = Math.ceil(
                    (ACCESS_RETRY_TIMEOUT - elapsed) / 1000
                );

                callInfoNotify(
                    `Stiamo preparando il tuo accesso... Attendi ${remainingSeconds} secondi prima di riprovare.`
                );

                return;
            }
        }

        setLoader(true);
        try {
            const { data: list } = await GetActivations();
            const current = list?.activations?.find(
                a => a.activationOid === activationOid
            );

            if (!current) {
                callErrorNotify("Attivazione non trovata.");
                await refreshSafe();
                return;
            }

            if (current.status === "Blocked") {
                await refreshSafe();
                return;
            }

            lastClicks[activationOid] = now;

            pushLoginClickEvent(
                companyClientCode,
                activeLicense
            );

            const { data: url } = await RemoteAccess({
                params: { id: activationOid }
            });


            if (url) {
                const popup = window.open(url, "_blank");

                if (!popup) {
                    callErrorNotify("Impossibile aprire la finestra.");
                    return;
                }
                openedWindows[activationOid] = popup;
                activeSessions[activationOid] = true;

                const interval = setInterval(() => {
                    try {
                        if (popup.closed) {
                            clearInterval(interval);

                            delete openedWindows[activationOid];
                            delete activeSessions[activationOid];

                            lastClicks[activationOid] = Date.now();
                        }
                    } catch {
                        clearInterval(interval);

                        delete openedWindows[activationOid];
                        delete activeSessions[activationOid];
                    }
                }, 1000);
            } else {
                callErrorNotify("URL non disponibile.");
            }

            await refreshSafe();

        } catch {
            callErrorNotify("Si è verificato un errore imprevisto");
        } finally {
            setLoader(false);
        }
    };

    const handleCompanyDetails = React.useCallback(() => {

        views('company', companyOid);

    }, [views, companyOid])

    const handleActivationUser = React.useCallback(() => {

        views('activation', {
            activationId: activationOid,
            companyName: companyName,
            companyOid: companyOid,
        });

    }, [views, activationOid, companyName, companyOid])

    const isCompanyAdmin = React.useMemo(() => roleName === AppInCloudRoles.CompanyAdmin, [roleName]);

    const openTooltipTitle = accessAllowed
        ? "Apri Danea Easyfatt"
        : "";

    return (
        <Box className={styles.container}>

            <VdsTooltip title={openTooltipTitle} disableHoverListener={!activeLicense} arrow>
                <Box
                    className={accessAllowed ? styles.header : styles.headerNotActive}
                    onClick={accessAllowed ? handleClick : undefined}
                    sx={{
                        cursor: accessAllowed ? 'pointer' : 'not-allowed',
                        ':hover': accessAllowed ? { opacity: 0.8 } : { opacity: 1 },
                    }}
                    aria-disabled={!accessAllowed}
                >
                    <Stack
                        direction="column"
                        spacing={1}
                        sx={{
                            pointerEvents: accessAllowed ? "auto" : "none"
                        }}
                    >
                        <Box className={styles.companyName}>{companyName}</Box>

                        {companyVatCode ? (
                            <Stack direction="row" spacing={1}>
                                <Box className={styles.vatLabel}>Partita IVA:</Box>
                                <Box className={styles.vatValue}>{companyVatCode}</Box>
                            </Stack>
                        ) : (
                            companyFiscalCode && (
                                <Stack direction="row" spacing={1}>
                                    <Box className={styles.vatLabel}>Codice fiscale:</Box>
                                    <Box className={styles.vatValue}>{companyFiscalCode}</Box>
                                </Stack>
                            )
                        )}

                        {cardMessage && (
                            <Box className={accessAllowed ? styles.accessAllowedMessage : styles.accessDeniedMessage}>
                                {cardMessage}
                            </Box>
                        )}
                    </Stack>
                </Box>
            </VdsTooltip>

            <Box className={styles.footer}>
                <Stack direction="row" alignItems="center">
                    {isCompanyAdmin && (
                        <VdsTooltip title={activeLicense ? "Licenza" : "Licenza scaduta"} arrow>
                            <IconButton
                                className={styles.footerButton}
                                onClick={handleActivationUser}
                            >
                                {activeLicense
                                    ? <TbLicense className={styles.footerButtonPrimary} />
                                    : <TbLicenseOff className={styles.footerButtonError} />}
                            </IconButton>
                        </VdsTooltip>
                    )}
                    {isCompanyAdmin && (
                        <VdsTooltip title="Dettagli" arrow>
                            <IconButton
                                className={styles.footerButton}
                                onClick={handleCompanyDetails}
                            >
                                <MdBusiness className={styles.footerButtonPrimary} />
                            </IconButton>
                        </VdsTooltip>
                    )}
                    <VdsTooltip title="Manuale Easyfatt" arrow>
                        <IconButton
                            className={styles.footerButton}
                            onClick={() => window.open("https://help.danea.it/easyfatt/#t=Easyfatt_Cloud.htm", "_blank")}
                        >
                            <MdMenuBook className={styles.footerButtonPrimary} />
                        </IconButton>
                    </VdsTooltip>
                </Stack>

                <Stack>
                    <VdsTooltip title={openTooltipTitle} disableHoverListener={!activeLicense} arrow>
                        <span style={{ display: 'inline-flex', verticalAlign: 'top' }}>
                            <IconButton
                                className={styles.daneaButton}
                                onClick={handleClick}
                                disabled={!accessAllowed}
                                aria-disabled={!accessAllowed}
                            >
                                <img src={daneaLogo} height="30px" alt="danea logo" />
                            </IconButton>
                        </span>
                    </VdsTooltip>
                </Stack>
            </Box>
        </Box>
    );
}
